import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { partnerDashboardService, Owner, CreateOwnerDto } from "../../../apis/partnerDashboard";

interface PartnerOwnersProps {
  partnerId: string;
}

const PartnerOwners: React.FC<PartnerOwnersProps> = ({ partnerId }) => {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [formData, setFormData] = useState<CreateOwnerDto>({
    name: "",
    phone: "",
    email: "",
    address: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    fetchOwners();
  }, [partnerId]);

  const fetchOwners = async () => {
    setLoading(true);
    try {
      const data = await partnerDashboardService.getOwners(partnerId);
      setOwners(data);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch owners",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOwner = () => {
    setFormData({ name: "", phone: "", email: "", address: "" });
    setOpenCreateModal(true);
  };

  const handleEditOwner = (owner: Owner) => {
    setSelectedOwner(owner);
    setFormData({
      name: owner.name,
      phone: owner.phone,
      email: owner.email || "",
      address: owner.address || "",
    });
    setOpenEditModal(true);
  };

  const handleDeleteOwner = (owner: Owner) => {
    setSelectedOwner(owner);
    setOpenDeleteDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone) {
      setSnackbar({
        open: true,
        message: "Please fill in name and phone",
        severity: "error",
      });
      return;
    }

    try {
      if (openCreateModal) {
        await partnerDashboardService.createOwner(formData);
        setSnackbar({
          open: true,
          message: "Owner created successfully",
          severity: "success",
        });
      } else if (selectedOwner) {
        await partnerDashboardService.updateOwner(selectedOwner._id, formData);
        setSnackbar({
          open: true,
          message: "Owner updated successfully",
          severity: "success",
        });
      }
      setOpenCreateModal(false);
      setOpenEditModal(false);
      setSelectedOwner(null);
      fetchOwners();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to save owner",
        severity: "error",
      });
    }
  };

  const confirmDelete = async () => {
    if (!selectedOwner) return;
    try {
      await partnerDashboardService.deleteOwner(selectedOwner._id);
      setSnackbar({
        open: true,
        message: "Owner deleted successfully",
        severity: "success",
      });
      setOpenDeleteDialog(false);
      setSelectedOwner(null);
      fetchOwners();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to delete owner",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">Car Owners</Typography>
        <Button variant="contained" color="primary" onClick={handleCreateOwner} startIcon={<Add />}>
          Add Owner
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : owners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No owners found
                </TableCell>
              </TableRow>
            ) : (
              owners.map((owner) => (
                <TableRow key={owner._id}>
                  <TableCell>{owner.name}</TableCell>
                  <TableCell>{owner.phone}</TableCell>
                  <TableCell>{owner.email || "-"}</TableCell>
                  <TableCell>{owner.address || "-"}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditOwner(owner)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteOwner(owner)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Modal */}
      <Dialog
        open={openCreateModal || openEditModal}
        onClose={() => {
          setOpenCreateModal(false);
          setOpenEditModal(false);
          setSelectedOwner(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{openCreateModal ? "Create Owner" : "Edit Owner"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCreateModal(false);
              setOpenEditModal(false);
              setSelectedOwner(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {openCreateModal ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Owner</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedOwner?.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerOwners;

